import React, { useMemo, useState } from 'react';
import { Modal, Select, Radio } from 'antd';
import ResultTable from '../../components/table/ResultTable';
import ResultHeader from '../../components/table/ResultHeader';
import ResultBody from '../../components/table/ResultBody';
import ResultRow from '../../components/table/ResultRow';
import ResultHeaderCell from '../../components/table/ResultHeaderCell';
import ResultBodyCell from '../../components/table/ResultBodyCell';
import HecRasHelper from '../../helpers/HecRasHelper';

/**
 * Render score with color
 */
const renderScore = (score) => {
    return (
        <span style={{ color: HecRasHelper.getScoreColor(score), fontWeight: 'bold' }}>
            {score.toFixed(2)}
        </span>
    );
};

export default function ScoreAnalysisModal({ visible, onClose, history }) {
    const [selectedMessageCodes, setSelectedMessageCodes] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('crossSection'); // 'crossSection', 'bridge', 'culvert', 'overall'
    
    const analysisData = useMemo(() => {
        if (!history) return null;

        const rows = HecRasHelper.buildItemScoreRows(history);

        const sortByRS = (a, b) => parseFloat(b.riverStation) - parseFloat(a.riverStation);
        const crossSectionData = [...rows.crossSection].sort(sortByRS);
        const bridgeData = [...rows.bridge].sort(sortByRS);
        const culvertData = [...rows.culvert].sort(sortByRS);
        const overallData = rows.overall;

        const allAllowedCodes = [
            ...new Set([
                ...HecRasHelper.ALLOWED_CROSS_SECTION_CODES,
                ...HecRasHelper.ALLOWED_BRIDGE_CODES,
                ...HecRasHelper.ALLOWED_CULVERT_CODES,
                ...HecRasHelper.ALLOWED_OVERALL_CODES,
            ])
        ].sort();

        return {
            crossSectionData,
            bridgeData,
            culvertData,
            overallData,
            crossSectionCount: crossSectionData.length,
            bridgeCount: bridgeData.length,
            culvertCount: culvertData.length,
            overallCount: overallData.length,
            allowedMessageCodes: allAllowedCodes,
        };
    }, [history]);
    
    // Get current data based on selected category
    const currentData = useMemo(() => {
        if (!analysisData) return [];
        if (selectedCategory === 'bridge') return analysisData.bridgeData;
        if (selectedCategory === 'culvert') return analysisData.culvertData;
        if (selectedCategory === 'overall') return analysisData.overallData;
        return analysisData.crossSectionData;
    }, [analysisData, selectedCategory]);
    
    // Get unique message codes from current data
    const messageCodeOptions = useMemo(() => {
        if (!currentData || currentData.length === 0) return [];
        const codes = new Set();
        currentData.forEach(item => {
            item.messages?.split(', ').forEach(code => {
                if (code.trim()) codes.add(code.trim());
            });
        });
        return Array.from(codes).sort().map(code => ({ label: code, value: code }));
    }, [currentData]);
    
    // Filter data based on selections
    const filteredData = useMemo(() => {
        if (!currentData || currentData.length === 0) return [];
        
        return currentData.filter(item => {
            // Filter by message codes
            if (selectedMessageCodes.length > 0) {
                const itemCodes = item.messages?.split(', ').map(c => c.trim()) || [];
                if (!selectedMessageCodes.some(code => itemCodes.includes(code))) {
                    return false;
                }
            }
            return true;
        });
    }, [currentData, selectedMessageCodes]);
    
    if (!history) {
        return (
            <Modal
                title="Score Analysis"
                open={visible}
                onCancel={onClose}
                footer={null}
                width={800}
                styles={{ body: { maxHeight: '70vh', overflow: 'auto' } }}
            >
                <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                    No history data available. Please run a check first.
                </div>
            </Modal>
        );
    }
    
    return (
        <Modal
            title="Score Analysis"
            open={visible}
            onCancel={onClose}
            footer={null}
            width={650}
            styles={{ body: { maxHeight: '70vh', overflow: 'auto' } }}
        >
            {(() => {
                const totalScore = HecRasHelper.calculateOverallScore(history);
                const totalScoreColor = HecRasHelper.getScoreColor(totalScore);
                return (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 16px',
                            marginBottom: 12,
                            background: '#fafafa',
                            border: '1px solid #f0f0f0',
                            borderRadius: 6,
                        }}
                    >
                        <span style={{ fontSize: 13, color: '#8c8c8c', fontWeight: 500 }}>Total Score</span>
                        <span style={{ fontSize: 22, fontWeight: 600, color: totalScoreColor, lineHeight: 1.2 }}>
                            {totalScore !== null && totalScore !== undefined ? totalScore.toFixed(2) : '-'}
                        </span>
                    </div>
                );
            })()}

            <div style={{ marginBottom: 12 }}>
                <Radio.Group
                    value={selectedCategory}
                    onChange={(e) => {
                        setSelectedCategory(e.target.value);
                        setSelectedMessageCodes([]); // Clear message code filter when category changes
                    }}
                    buttonStyle="solid"
                >
                    <Radio.Button value="crossSection">Cross Section</Radio.Button>
                    <Radio.Button value="bridge">Bridge</Radio.Button>
                    <Radio.Button value="culvert">Culvert</Radio.Button>
                    <Radio.Button value="overall">Overall Flow and Geometry</Radio.Button>
                </Radio.Group>
            </div>
            
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <Select
                    size='small'
                    mode="multiple"
                    allowClear
                    placeholder="Filter by Message Code..."
                    value={selectedMessageCodes}
                    onChange={setSelectedMessageCodes}
                    options={messageCodeOptions}
                    style={{ flex: 1 }}
                    maxTagCount={3}
                    showSearch
                    filterOption={(input, option) =>
                        option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                />
            </div>
            
            {(() => {
                const total = currentData?.length || 0;
                const hasItems = total > 0;
                const avg = hasItems
                    ? currentData.reduce((acc, r) => acc + r.score, 0) / total
                    : null;
                const categoryLabel = selectedCategory === 'bridge' ? 'Bridge'
                    : selectedCategory === 'culvert' ? 'Culvert'
                    : selectedCategory === 'overall' ? 'Overall'
                    : 'Cross Section';
                const uniqueCodes = new Set();
                (currentData || []).forEach(item => {
                    item.messages?.split(', ').forEach(code => {
                        const c = code.trim();
                        if (c) uniqueCodes.add(c);
                    });
                });
                const avgColor = HecRasHelper.getScoreColor(avg);
                const cardStyle = {
                    flex: 1,
                    padding: '12px 16px',
                    background: '#fff',
                    border: '1px solid #f0f0f0',
                    borderRadius: 6,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                };
                const labelStyle = { fontSize: 12, color: '#8c8c8c', marginBottom: 4 };
                const valueStyle = { fontSize: 22, fontWeight: 600, lineHeight: 1.2 };
                return (
                    <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                        <div style={cardStyle}>
                            <div style={labelStyle}>Total {categoryLabel}</div>
                            <div style={{ ...valueStyle, color: '#262626' }}>{total}</div>
                        </div>
                        <div style={cardStyle}>
                            <div style={labelStyle}>Unique Message IDs</div>
                            <div style={{ ...valueStyle, color: '#262626' }}>{uniqueCodes.size}</div>
                        </div>
                        <div style={cardStyle}>
                            <div style={labelStyle}>Avg Score</div>
                            <div style={{ ...valueStyle, color: avgColor }}>{avg !== null ? avg.toFixed(2) : '-'}</div>
                        </div>
                    </div>
                );
            })()}

            {(!currentData || currentData.length === 0) ? (
                <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>No data available for this category</div>
            ) : (
                <ResultTable scrollable={{ height: 400 }} style={{ width: '100%' }} maxWidth={'100%'}>
                    <ResultHeader>
                        <ResultRow>
                            <ResultHeaderCell scrollable={{ top: 0 }} style={{ textAlign: 'left', width: HecRasHelper.getWidth(selectedCategory === 'overall' ? 30 : 10) }}>River Station</ResultHeaderCell>
                            {/* <ResultHeaderCell scrollable={{ top: 0 }} style={{ textAlign: 'left', width: HecRasHelper.getWidth(10) }}>Type</ResultHeaderCell> */}
                            <ResultHeaderCell scrollable={{ top: 0 }} style={{ textAlign: 'right', width: HecRasHelper.getWidth(6) }}>Score</ResultHeaderCell>
                            {selectedCategory !== 'overall' && (
                                <ResultHeaderCell scrollable={{ top: 0 }} style={{ textAlign: 'left', width: HecRasHelper.getWidth(60) }}>Messages</ResultHeaderCell>
                            )}
                        </ResultRow>
                    </ResultHeader>
                    <ResultBody>
                        {filteredData.map((row, index) => (
                            <ResultRow key={`${row.type}-${row.riverStation}-${index}`}>
                                <ResultBodyCell style={{ textAlign: 'left', width: HecRasHelper.getWidth(selectedCategory === 'overall' ? 30 : 10) }}>{row.riverStation}</ResultBodyCell>
                                {/* <ResultBodyCell style={{ textAlign: 'left', width: HecRasHelper.getWidth(10) }}>{row.type}</ResultBodyCell> */}
                                <ResultBodyCell style={{ textAlign: 'right', width: HecRasHelper.getWidth(6) }}>{renderScore(row.score)}</ResultBodyCell>
                                {selectedCategory !== 'overall' && (
                                    <ResultBodyCell style={{ textAlign: 'left', width: HecRasHelper.getWidth(60) }}>{row.messages}</ResultBodyCell>
                                )}
                            </ResultRow>
                        ))}
                    </ResultBody>
                </ResultTable>
            )}
            
            {(() => {
                const codesByCategory = {
                    crossSection: HecRasHelper.ALLOWED_CROSS_SECTION_CODES,
                    bridge: HecRasHelper.ALLOWED_BRIDGE_CODES,
                    culvert: HecRasHelper.ALLOWED_CULVERT_CODES,
                    overall: HecRasHelper.ALLOWED_OVERALL_CODES,
                };
                const categoryCodes = [...(codesByCategory[selectedCategory] || [])].sort();
                if (categoryCodes.length === 0) return null;
                return (
                <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                    <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 13 }}>
                        Message Codes ({categoryCodes.length}):
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {categoryCodes.map(code => (
                            <span 
                                key={code} 
                                style={{ 
                                    padding: '2px 4px', 
                                    backgroundColor: '#fff', 
                                    border: '1px solid #d9d9d9', 
                                    borderRadius: 4,
                                    fontSize: 12
                                }}
                            >
                                {code}
                            </span>
                        ))}
                    </div>
                </div>
                );
            })()}
        </Modal>
    );
}
